import React, { useMemo, useState } from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function ResetPassword() {
  const query = useQuery();
  const params = useParams();
  const navigate = useNavigate();

  // Supports:
  // 1) /reset-password?token=...
  // 2) /reset-password/:token
  const token = (query.get("token") || params.token || "").trim();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [status, setStatus] = useState({ type: "idle", message: "" });
  const [loading, setLoading] = useState(false);

  // ✅ Always works (doesn't rely on Vercel rewrites)
  const API_BASE = useMemo(() => {
    const raw = (process.env.REACT_APP_API_URL || "").trim();
    const cleaned = raw.replace(/\/$/, "").replace(/\/api$/, ""); // prevent .../api/api
    return cleaned || "https://streamsite-v8dd.onrender.com";
  }, []);

  const canSubmit =
    token && password.length >= 8 && confirm.length >= 8 && password === confirm;

  async function onSubmit(e) {
    e.preventDefault();
    setStatus({ type: "idle", message: "" });

    if (!token) {
      setStatus({
        type: "error",
        message: "Missing token. Use the reset link from your email.",
      });
      return;
    }
    if (password.length < 8) {
      setStatus({
        type: "error",
        message: "Password must be at least 8 characters.",
      });
      return;
    }
    if (password !== confirm) {
      setStatus({ type: "error", message: "Passwords do not match." });
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setStatus({
          type: "error",
          message:
            data?.error || "Reset failed. Your link may be expired. Try again.",
        });
        return;
      }

      setStatus({
        type: "success",
        message: "Password updated! Redirecting to sign in…",
      });

      setTimeout(() => navigate("/signin"), 900);
    } catch (err) {
      setStatus({
        type: "error",
        message: "Network error resetting password. Try again.",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <section style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.title}>Reset password</h1>
        <p style={styles.subtitle}>Choose a new password for your account.</p>

        {!token && (
          <div style={styles.error}>
            Missing token. Use the reset link from your email.
          </div>
        )}

        <form onSubmit={onSubmit} style={{ display: "grid", gap: 12, marginTop: 12 }}>
          <label style={styles.label}>
            New password
            <div style={{ display: "flex", gap: 8 }}>
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type={showPw ? "text" : "password"}
                autoComplete="new-password"
                placeholder="At least 8 characters"
                style={{ ...styles.input, flex: 1 }}
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                style={styles.showBtn}
                aria-label={showPw ? "Hide password" : "Show password"}
                title={showPw ? "Hide" : "Show"}
              >
                {showPw ? "🙈" : "👁️"}
              </button>
            </div>
          </label>

          <label style={styles.label}>
            Confirm password
            <input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              type={showPw ? "text" : "password"}
              autoComplete="new-password"
              placeholder="Re-type password"
              style={styles.input}
            />
          </label>

          <button
            type="submit"
            disabled={loading || !canSubmit}
            style={{
              ...styles.primaryBtn,
              cursor: loading || !canSubmit ? "not-allowed" : "pointer",
              opacity: loading || !canSubmit ? 0.6 : 1,
            }}
          >
            {loading ? "Updating..." : "Update password"}
          </button>

          {status.type !== "idle" && (
            <div style={status.type === "success" ? styles.success : styles.error}>
              {status.message}
            </div>
          )}

          <div style={{ marginTop: 4 }}>
            <Link to="/signin" style={styles.link}>
              Back to Sign in
            </Link>
          </div>
        </form>
      </div>
    </section>
  );
}

const styles = {
  page: {
    minHeight: "calc(100vh - 90px)",
    display: "grid",
    placeItems: "center",
    padding: "24px 16px",
  },
  card: {
    width: "min(640px, 92vw)",
    background: "rgba(0,0,0,0.62)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 16,
    padding: 20,
    boxShadow: "0 16px 40px rgba(0,0,0,0.55)",
    backdropFilter: "blur(10px)",
  },
  title: { margin: 0, fontSize: 34, letterSpacing: 0.2 },
  subtitle: { margin: "8px 0 0 0", opacity: 0.85, lineHeight: 1.35 },
  label: { display: "grid", gap: 6, fontSize: 14, opacity: 0.95 },
  input: {
    width: "100%",
    padding: "12px 12px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.16)",
    background: "rgba(255,255,255,0.06)",
    color: "white",
    outline: "none",
  },
  showBtn: {
    padding: "12px 12px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.16)",
    background: "rgba(255,255,255,0.06)",
    color: "white",
    cursor: "pointer",
    fontWeight: 800,
    minWidth: 56,
  },
  primaryBtn: {
    padding: "12px 14px",
    borderRadius: 12,
    border: "none",
    cursor: "pointer",
    fontWeight: 800,
    background: "#1fa2ff",
    color: "black",
  },
  link: { color: "#7cc7ff", textDecoration: "none", fontSize: 14 },
  error: {
    marginTop: 12,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(255,80,80,0.35)",
    background: "rgba(255,80,80,0.12)",
    color: "white",
  },
  success: {
    marginTop: 12,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(120,255,160,0.25)",
    background: "rgba(120,255,160,0.10)",
    color: "white",
  },
};
